
import { getAccessToken } from "config/token";
import { IMsalContext } from "@azure/msal-react";
import axios, { AxiosResponse } from "axios";
import { Credentials } from "../components/Users/components/AccessData/types";
import { NEXUS_API_BASE_URL, SECRET_KEY_FOR_API } from "const";

/**
 * Derive an AES-GCM key from a passphrase using PBKDF2.
 */
async function getCryptoKey(): Promise<CryptoKey> {
  const passphrase = SECRET_KEY_FOR_API;   
  const salt       = new TextEncoder().encode("fixed-salt");  // could be per-user
  const baseKey = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(passphrase),
    "PBKDF2",
    false,
    ["deriveKey"],
  );
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", hash: "SHA-256", salt, iterations: 100_000 },
    baseKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

/**
 * Encrypt a JSON string and return a base64 blob containing iv+ciphertext.
 */
export async function encryptSession(plaintext: string): Promise<string> {
  const key = await getCryptoKey();
  const iv  = crypto.getRandomValues(new Uint8Array(12));
  const ct  = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    new TextEncoder().encode(plaintext),
  );
  const combined = new Uint8Array(iv.byteLength + ct.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(ct), iv.byteLength);
  return btoa(String.fromCharCode(...combined));
}

/**
 * Decrypt the base64 blob back into the original JSON string.
 */
async function decryptSession(blob: string): Promise<string> {
  const data = Uint8Array.from(atob(blob), c => c.charCodeAt(0));
  const iv   = data.slice(0, 12);
  const ct   = data.slice(12);
  const key  = await getCryptoKey();
  const pt   = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv },
    key,
    ct,
  );
  return new TextDecoder().decode(pt);
}

/**
 * Encrypts and stores the AWS session in localStorage under "awsSession".
 */
export async function storeAwsSession(raw: Credentials): Promise<void> {
  const blob = await encryptSession(JSON.stringify(raw));
  localStorage.setItem("awsSession", blob);
}

/**
 * Loads, decrypts, parses, and validates the AWS session.
 * Throws if missing, invalid, or expired (with 60s buffer).
 */
export async function loadAwsSession(): Promise<Credentials> {
  const blob = localStorage.getItem("awsSession");

  if (!blob) {
    throw new Error("No AWS session stored");
  }

  let json: string;
  try {
    json = await decryptSession(blob);
   
  } catch (err) {
    throw new Error("Failed to decrypt AWS session");
  }

  if (!json) {
    throw new Error("Decrypted session is empty");
  }

  let session: Credentials;
  try {
    session = JSON.parse(json);
  } catch (err) {

    throw new Error("Invalid AWS session JSON");
  }

  // Validate expiration (60s buffer)
  if (Date.now() / 1000 > session.ExpirationTimestamp - 60) {
    throw new Error("AWS credentials expired");
  }

  return session;
}



export const generateKeysApi = async (
  msalInstance: IMsalContext,
): Promise<any> => {
  const token = await getAccessToken(msalInstance);
  const bearerToken = "Bearer " + (token == undefined ? "" : token.token);
  const requestURL: string = NEXUS_API_BASE_URL + "/nexus/get-keys";

  const config = {
    headers: { Authorization: bearerToken },
  };

  let keysResponse:unknown = null;

  try {
    const res: AxiosResponse = await axios.get(requestURL, config);
    keysResponse = res.data;
    console.log("Response from /nexus/get-keys:", keysResponse);
    return keysResponse;
  } catch (error) {
    console.error("Error fetching Keys:", error);
    keysResponse = null;
    return keysResponse;
  }
};
